/* eslint-disable react/jsx-props-no-spreading */
import { EnumCellProps } from '@jsonforms/core';
import { useJsonForms } from '@jsonforms/react';
import React, { useEffect, useRef, useState } from 'react';
import { SpectrumInputProps } from './index';

// Styles
import {
    ActionButton,
    Checkbox,
    CheckboxGroup,
    SearchField,
} from '@adobe/react-spectrum';

import lodash from 'lodash';

import Alert from '@spectrum-icons/workflow/Alert';
import Asterisk from '@spectrum-icons/workflow/Asterisk';
import ChevronDown from '@spectrum-icons/workflow/ChevronDown';
import './InputMultiSelect.css';

interface IMultiSelectOptions extends EnumCellProps {
    options: {
        label: string;
        value: any;
    }[];
}

type optionsType = {
    // form element name
    name: string;
    // label to be shown for the editor
    label?: string;
    // boolean value to disable the multi select
    disabled?: boolean;
    // placeholder for the multi select
    placeholder?: string;
    // search options
    search?: {
        isVisible: true;
        placeholder: string;
        spectrumProps: any;
        noResults: string;
    };
};

export const InputMultiSelect = (
    props: SpectrumInputProps & IMultiSelectOptions
) => {
    const {
        // config,
        uischema,
        options,
        data,
        // isValid,
        // id,
        // enabled,
        required,
        path,
        handleChange,
        label,
        placeholder = "Select an option...",
        // validationState,
        errorMessage,
        // description,
    } = props;

    const {
        disabled,
        search = {
            isVisible: false,
            placeholder: '',
            spectrumProps: {},
            noResults: '',
        },
    } = uischema.options as optionsType;

    const jsonFormContext = useJsonForms();
    const [touched, setTouched] = useState(false);
    const [isMenuOpen, setMenuOpen] = useState(false);
    const [checkboxData, setCheckboxData] = useState<any>([]);
    const [list, setList] = useState(options);
    const { config: { formTouched = false } = {} } = jsonFormContext || {};
    const hasTouched = formTouched || touched;
    const menuRef: any = useRef(null);

    useEffect(() => {
        // When the form is reset, the formTouched is set to false, then make the field as untouched
        if (formTouched === false && touched === true) {
          setTouched(false);
        }
      }, [formTouched]);    

    /** callback to set entire selected  items back to main page  */
    const multiselectCallBack =
        jsonFormContext?.config?.lifecycleHooks?.onSuccessCallbacks
            ?.multiselectCallBack;
    useEffect(() => {
        if (multiselectCallBack) {
            /** This callback sets/unset the multiselect key items  to  page component */
            multiselectCallBack(name, checkboxData);
        }
    }, [checkboxData]);

    const closeMenu = () => {
        setMenuOpen(false);
        setTouched(true);
    };

    const toggleMenu = () => {
        if (isMenuOpen) {
            closeMenu();
        } else {
            setMenuOpen(true);
        }
    };

    useEffect(() => {
        // If the options get changed from outside, sync it
        if (!lodash.isEqual(options, list)) {
            setList(options);
        }
    }, [options]);

    useEffect(() => {
        const onOutsidePress = (e: Event) => {
            if (
                isMenuOpen &&
                menuRef.current &&
                !menuRef.current.contains(e.target)
            ) {
                closeMenu();
            }
        };
        const onEscPress = (e: KeyboardEvent) => {
            if (e.key === 'Escape') {
                if (isMenuOpen) {
                    closeMenu();
                }
            }
        };
        document.addEventListener('click', onOutsidePress, true);
        document.addEventListener('mousedown', onOutsidePress);
        document.addEventListener('keyup', onOutsidePress);
        document.addEventListener('keyup', onEscPress);

        return () => {
            // Cleanup the event listener
            document.removeEventListener('click', onOutsidePress, true);
            document.removeEventListener('mousedown', onOutsidePress);
            document.removeEventListener('keyup', onOutsidePress);
            document.removeEventListener('keyup', onEscPress);
        };
    }, [isMenuOpen]);

    const hasError = errorMessage && hasTouched;

    const selectedLabels = options.filter((item: any) =>
        data?.includes(item.value)
    ).map((item: any) => item.label);

    const selectedValues = selectedLabels?.join(', ');
    /**
     * method to track checkbox changes with in checkbox group to emit item to main components
     * @param checkedProps
     */
    const setCheckedValues = (checkedProps: any) => {
        const { val: isChecked, item: itemObject } = checkedProps;
        const selectedCheckboxDataCopy = [...checkboxData];
        if (isChecked) {
            selectedCheckboxDataCopy.push({
                id: itemObject.id,
                name: itemObject.name,
            });
            setCheckboxData(selectedCheckboxDataCopy);
        } else {
            const arrayIndex = selectedCheckboxDataCopy.findIndex(
                (checkItem) => checkItem.id === itemObject?.id
            );
            if (arrayIndex) selectedCheckboxDataCopy.splice(arrayIndex, 1);
            setCheckboxData(selectedCheckboxDataCopy);
        }
    };
    const validationState = () => {
        if (required && hasTouched) {
            if (data?.length > 0) {
                return 'valid';
            }
            return 'invalid';
        }
        return undefined;
    };

    /**
     * function to be called on search
     * filters the options based on the search term
     * @param searchTerm: string
     */
    const searchHandler = (searchTerm: any) => {
        const newList = options.filter((item: any) => {
            if (item.label.toLowerCase().includes(searchTerm.toLowerCase())) {
                return true;
            }
            return false;
        });
        setList(newList);
    };

    const handleMultiSelectChange = (value: any) => {
        handleChange(path, value.length ? value : undefined);
    };

    return (
        <div className='multi_select'>
            <div className={'outer_wrapper'} id={`form-input-${path}`}>
                <div className={'label'}>
                    {label}
                    {required && (
                        <>
                            &nbsp;&nbsp;
                            <Asterisk UNSAFE_className={'required'} />
                        </>
                    )}
                </div>
                <div ref={menuRef}>
                    <ActionButton
                        data-testid='multiselect-action-btn'
                        isDisabled={disabled}
                        UNSAFE_className={`${'custom_list_picker_button'} ${hasError ? 'invalid' : ''
                            }`}
                        onPress={toggleMenu}
                    >
                        {!selectedValues ? (
                            <span className={'custom_list_picker_placeholder'}>
                                {placeholder}
                            </span>
                        ) : (
                            <span className={'custom_list_picker_values'}>
                                {selectedValues}
                            </span>
                        )}
                        {hasError && <Alert UNSAFE_className={'error_icon'} />}
                        <ChevronDown UNSAFE_className={'down_icon'} />
                    </ActionButton>

                    <div
                        data-testid='multiselect-group-btn'
                        className={`${'menu_wrapper'} ${isMenuOpen ? 'is_open' : ''}`}
                    >
                        {search && search.isVisible && (
                            <div className={'searchFieldWrap'}>
                                <SearchField
                                    placeholder={search.placeholder}
                                    aria-label='searchfield'
                                    data-testid='searchField'
                                    onChange={searchHandler}
                                    UNSAFE_className={'searchField'}
                                    {...search.spectrumProps}
                                />
                            </div>
                        )}
                        {search && search.isVisible && !list.length && (
                            <div className={'noResults'}>{search.noResults}</div>
                        )}
                        <CheckboxGroup
                            label=''
                            aria-label='checkbox-group'
                            value={data || []}
                            onChange={handleMultiSelectChange}
                            UNSAFE_className={'chkGroup'}
                            isRequired={required}
                            validationState={validationState()}
                        >
                            {list.map((item: any) => (
                                <Checkbox
                                    UNSAFE_className='groupchkbox'
                                    value={item.value}
                                    key={item.value}
                                    onChange={(val: boolean) => {
                                        setCheckedValues({ val, item });
                                    }}
                                >
                                    {item.label}
                                </Checkbox>
                            ))}
                        </CheckboxGroup>
                    </div>
                    {hasError && errorMessage && (
                        <span className={'error'}>{errorMessage}</span>
                    )}
                </div>
            </div>
        </div>
    );
};
